import { AirVent, Bath, HeartHandshake, Leaf, Sun, Wifi } from "lucide-react";
import { ChapterHeading, Reveal } from "./Reveal";

const HERITAGE_IMG = "/images/mural.jpg";
const ROOM_IMG = "/images/room.jpg";

const AMENITIES = [
  { icon: AirVent, label: "Air Conditioning" },
  { icon: Bath, label: "Private Bathrooms" },
  { icon: Wifi, label: "Free WiFi" },
  { icon: Sun, label: "Terrace" },
  { icon: Leaf, label: "Garden" },
  { icon: HeartHandshake, label: "LGBTQ+ Friendly" },
];

export const About = () => (
  <section id="about" data-testid="about-section" className="mx-auto max-w-7xl px-6 py-28 md:px-10 md:py-40">
    <ChapterHeading number="01" label="The House" testId="about-chapter" />

    <div className="grid gap-16 md:grid-cols-12 md:gap-12">
      <Reveal className="md:col-span-5">
        <h2 className="font-serif text-4xl leading-[1.05] tracking-tight text-[#F5F5F0] sm:text-5xl lg:text-6xl">
          Brass, by-lanes &amp; <em className="font-light italic text-[#C5A059]">Banarasi</em> warmth.
        </h2>
      </Reveal>
      <Reveal delay={0.15} className="md:col-span-6 md:col-start-7">
        <p data-testid="about-story" className="text-base leading-loose text-[#A1A1AA] md:text-lg">
          Hosted by <span className="text-[#F5F5F0]">Sagar Agrawal</span>, Pipewalas
          House sits deep inside Thatheri Bazar — the centuries-old metalworkers&rsquo;
          quarter of Kashi, where the ring of brass being shaped still scores the
          afternoon. It is a lived-in family home turned homestay: unhurried,
          welcoming, and quietly proud of its city.
        </p>
        <p className="mt-6 text-base leading-loose text-[#A1A1AA] md:text-lg">
          Step out and you are in the maze that makes Varanasi — silk sellers,
          sweet shops, temple bells — yet inside, the house stays calm. Every
          traveller is welcome here.
        </p>
        <p className="mt-6 text-base leading-loose text-[#A1A1AA] md:text-lg">
          No frills and no inflated tariffs — clean sheets, working AC, hot
          water and a fair price, so you can spend on the city, not the room.
        </p>
        <div data-testid="about-value-chips" className="mt-8 flex flex-wrap gap-3">
          {["Honest rates", "Family-run", "Walk to the ghats"].map((chip) => (
            <span
              key={chip}
              className="rounded-full border border-[#C5A059]/30 px-4 py-1.5 text-xs font-medium tracking-wide text-[#C5A059]"
            >
              {chip}
            </span>
          ))}
        </div>
      </Reveal>
    </div>

    <div className="mt-20 grid gap-5 md:grid-cols-3">
      <Reveal className="group relative overflow-hidden rounded-2xl">
        <img
          src={HERITAGE_IMG}
          alt="Golden Kashi Vishwanath mural wall inside a room at Pipewalas House"
          className="h-[420px] w-full object-cover transition-transform duration-700 group-hover:scale-[1.03] md:h-[560px]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#080809]/70 to-transparent" />
        <p className="absolute bottom-5 left-5 font-serif text-lg italic text-[#F5F5F0]/90">
          Kashi on the walls, calm in the rooms
        </p>
      </Reveal>

      <div className="flex flex-col gap-5 md:col-span-2">
        <Reveal delay={0.1} className="group relative overflow-hidden rounded-2xl">
          <img
            src={ROOM_IMG}
            alt="Family room at Pipewalas House & Homestay"
            className="h-64 w-full object-cover transition-transform duration-700 group-hover:scale-[1.03] md:h-72"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#080809]/70 to-transparent" />
          <p className="absolute bottom-5 left-5 font-serif text-lg italic text-[#F5F5F0]/90">
            Rooms that let you rest
          </p>
        </Reveal>

        <div className="grid flex-1 grid-cols-2 gap-5 sm:grid-cols-3">
          {AMENITIES.map((a, i) => (
            <AmenityCard key={a.label} {...a} delay={0.15 + i * 0.07} />
          ))}
        </div>
      </div>
    </div>
  </section>
);

const AmenityCard = ({ icon: Icon, label, delay }) => (
  <Reveal
    delay={delay}
    data-testid={`amenity-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
    className="flex flex-col justify-between gap-6 rounded-2xl border border-white/5 bg-[#121214] p-6 transition-[transform,border-color] duration-500 hover:scale-[1.02] hover:border-[#C5A059]/30"
  >
    <Icon className="h-5 w-5 text-[#C5A059]" />
    <span className="text-sm font-medium text-[#F5F5F0]">{label}</span>
  </Reveal>
);
